import styled from 'styled-components';
import { CheckBox, Paragraph, useErrorHandling, useValidation } from '@lower-financial/core-components';
import { LoanPurpose } from '@lower-financial/lending-web-api/generated/models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { objectContainsFalsyPropsForKeys } from '@lower-financial/toolbox';
import { ENV } from '@lightspeed/environment';
import { IdentityForm } from '@lightspeed/components/ui/pages/credit-check/identity-form';
import { Layout } from '@lightspeed/components/ui/organisms/layout';
import {
  mortgageApplicationSchema,
} from '@lightspeed/contexts/mortgage-application-context/mortgage-application-validation';
import {
  MortgageApplicationStoreKeys,
} from '@lightspeed/contexts/mortgage-application-context/use-mortgage-application-state';
import { NextStepButton } from '@lightspeed/components/shared/buttons/next-step-button';
import { PageLayout } from '@lightspeed/components/shared/page-layout/page-layout';
import { Testimonial } from '@lightspeed/components/ui/molecules/testimonial';
import { useMortgageAnalytics } from '@lightspeed/hooks/useMortgageAnalytics/use-mortgage-analytics';
import { useMortgageApplication } from '@lightspeed/contexts/mortgage-application-context/mortgage-application-context';
import { useNextRoute } from '@lightspeed/routing/useNextRoute';
import {
  usePartnerConfiguration,
} from '@lightspeed/contexts/partner-configuration-context/partner-configuration-context';
import { useSubmitQuotingApplication } from '@lightspeed/hooks/useSubmitQuotingApplication/useSubmitQuotingApplication';
import { useUpsertApplication } from '@lightspeed/hooks/useUpsertApplication/useUpsertApplication';

const buttonDisclaimer = (
  <>
    <Paragraph variant={'smallLight'}>I agree to a soft credit check.</Paragraph>
    <Paragraph variant={'smallLight'}>(No hard credit check until you’re ready.)</Paragraph>
  </>
);

const CreditCheckPurchaseTestimonial = () => (
  <Testimonial
    title={'Guess who’s a new homeowner! We are!!!!'}
    content={'What made my experience great was that my loan advisor was always reliable and there when we needed him most! First time home buying can be stressful, but he was so very helpful, and I highly recommend him and the Lower team for your next or newest home purchase! Thank you!'}
    author={'Turquoise Dixon'}
  />
);

const CreditCheckHelocTestimonial = () => (
  <Testimonial
    title={'Solid HELOC Experience'}
    content={'Overall I had a great experience with Lower closing my HELOC. Our advisor was a great resource and very helpful throughout the entire process.'}
    author={'Conor McFarland'}
  />
);

export function CreditCheck() {
  const borrowerKeys: MortgageApplicationStoreKeys[] = [
    'borrowerSocialSecurityNumber',
    'borrowerDateOfBirth',
  ];

  const { goToNextRoute } = useNextRoute();
  const { mortgageApplication, updateMortgageApplication } = useMortgageApplication();
  const {
    partnerConfiguration: {
      isPartnerExperience, consentRequired, displayName,
    },
  } = usePartnerConfiguration();
  const { upsertApplication } = useUpsertApplication();
  const handleError = useErrorHandling();

  const fireAnalyticsEvent = useMortgageAnalytics('credit_check');
  const navigate = useNavigate();
  const submitQuotingApplication = useSubmitQuotingApplication();

  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [consented, setConsented] = useState(
    mortgageApplication.consentSharePreapprovalDenialReasons
    ?? (isPartnerExperience && consentRequired),
  );
  const [validate, errors] = useValidation(mortgageApplicationSchema, {
    keysToValidate: borrowerKeys,
  });

  const submissionExecutedRef = useRef(false);

  const disableNextButton = isLoading || objectContainsFalsyPropsForKeys(
    mortgageApplication,
    borrowerKeys,
  );

  const submitForm = useCallback(
    () => {
      if (validate(mortgageApplication).success) {
        setIsLoading(true);
        const currentDateTime = (new Date()).toISOString();
        updateMortgageApplication('borrowerCreditAuthDateTime', currentDateTime);

        updateMortgageApplication('consentSharePreapprovalDenialReasons', consented);
        setFormSubmitted(true);
      }
    },
    [consented, mortgageApplication, updateMortgageApplication, validate],
  );

  const denialConsentCheckbox = useMemo(
    () => {
      if (!isPartnerExperience || !consentRequired) { return undefined; }
      return (
        <CheckBox
          id={'consentSharePreapprovalDenialReasons'}
          label={(
            <>
              Send {displayName} updates. I agree to let Lower, LLC (Lower)
              share info to {displayName} on the final result of my application.&nbsp;
              <SeeDetailsLink
                href={`${ENV.PARTNER_DENIAL_CONSENT_DETAILS_URL}?partner=${encodeURIComponent('displayName')}`}
                target={'_blank'}
                onClick={(e) => e.stopPropagation()}
              >
                See the details.
              </SeeDetailsLink>
            </>
          )}
          checked={consented}
          onChange={(newVal) => setConsented(newVal)}
        />
      );
    },
    [consentRequired, consented, displayName, isPartnerExperience],
  );

  useEffect(() => {
    // when upsertApplication creates a new application, it will update the mortgage application
    // context with the ID it receives from the response. This will cause upsertApplication to
    // change since it depends on mortgageApplication, which will cause this useEffect to run a second time
    if (formSubmitted && !submissionExecutedRef.current) {
      submissionExecutedRef.current = true;
      (async () => {
        setIsLoading(true);
        await handleError({
          onRetryableError: () => {
            setIsLoading(false);
          },
          tryFn: async () => {
            const quotingApplicationId = await upsertApplication();
            await submitQuotingApplication(quotingApplicationId);
            fireAnalyticsEvent('next_button_success');
            goToNextRoute();
          },
        });
      })();
    }
  }, [
    fireAnalyticsEvent,
    formSubmitted,
    goToNextRoute,
    handleError,
    navigate,
    submitQuotingApplication,
    upsertApplication,
  ]);

  const submitButton = useMemo(
    () => (
      <NextStepButton
        isLoading={isLoading}
        disabled={disableNextButton}
        onClick={submitForm}
        label={'Submit'}
        ariaLabel={'I agree to a soft credit check and submit'}
      />
    ),
    [disableNextButton, isLoading, submitForm],
  );

  const tosLink = (
    <a
      href={'https://www.lower.com/legal/website-terms-of-use'}
      target={'_blank'}
      rel={'noreferrer'}
    >Terms of Service
    </a>
  );
  const privacyLink = (
    <a
      href={'https://www.lower.com/legal/privacy-policy'}
      target={'_blank'}
      rel={'noreferrer'}
    >Privacy Policy
    </a>
  );
  const arrayDisclaimer = (
    <>
      <p>
        By clicking the submit button above, I authorize Lower and Array US, Inc. to
        obtain and monitor my credit file from one or more consumer reporting agencies on a recurring basis for the
        purpose of verifying my identity, monitoring my credit profile, displaying my credit information to me,
        and providing personalized financial education or product recommendations. I further authorize Lower to obtain
        a soft credit report to evaluate potential eligibility for loan products based on the information provided.
        This authorization does not constitute a loan application or a credit decision. I agree to
        the {tosLink} and and acknowledge receipt of the {privacyLink}.
      </p>

      <p>
        I understand that Array US, Inc. is an independent third-party provider and is not affiliated with Lower.
      </p>

      <p>
        In connection with processing your loan application, we will request a consumer credit report on your credit
        standing, credit capacity and creditworthiness from a Consumer Reporting Agency. If adverse action is taken
        on your loan application, we will advise you of the name, address and telephone number of the Consumer Reporting
        Agency and you will have the right to obtain within 60 days of adverse action a free copy of the consumer credit
        report from the Consumer Reporting Agency. You also have the right to dispute the accuracy or completeness of
        any information in the credit report furnished.
      </p>
    </>
  );

  const subtitle = (
    <>
      To talk specifics, we need to verify your info, but it won’t impact your credit. Scout’s honor.&nbsp;👌
    </>
  );

  return (
    <Layout>
      <PageLayout
        title={<>Last step. Simple soft credit check.</>}
        subtitle={subtitle}
        button={submitButton}
        buttonDisclaimer={buttonDisclaimer}
        disclaimer={arrayDisclaimer}
        consentCheckbox={denialConsentCheckbox}
        desktopSidebar={mortgageApplication.loanPurpose === LoanPurpose.Purchase
          ? <CreditCheckPurchaseTestimonial />
          : <CreditCheckHelocTestimonial />
        }
      >
        <IdentityForm
          errors={errors}
          disabled={isLoading}
        />
      </PageLayout>
    </Layout>
  );
}

const SeeDetailsLink = styled.a`
  color: var(--body);
  font-weight: bold;
`;
